import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";

// Ceremony start: 2026-08-09 13:00 local (Europe/Bucharest UTC+3 in August = DST)
const TARGET = new Date("2026-08-09T13:00:00+03:00").getTime();

const pad = (n) => String(n).padStart(2, "0");

function getParts() {
  const diff = TARGET - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, ended: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, ended: false };
}

export default function Countdown() {
  const { t } = useLang();
  const [parts, setParts] = useState(getParts());

  useEffect(() => {
    const i = setInterval(() => setParts(getParts()), 1000);
    return () => clearInterval(i);
  }, []);

  const items = [
    { label: t.countdown.days, value: parts.days, digits: 3 },
    { label: t.countdown.hours, value: parts.hours, digits: 2 },
    { label: t.countdown.minutes, value: parts.minutes, digits: 2 },
    { label: t.countdown.seconds, value: parts.seconds, digits: 2 },
  ];

  return (
    <section
      className="relative py-24 md:py-32 px-6 md:px-12"
      data-testid="countdown-section"
    >
      <div className="max-w-5xl mx-auto text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="text-xs uppercase tracking-[0.4em] text-burgundy mb-4"
        >
          {t.countdown.subtitle}
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, delay: 0.1 }}
          className="font-serif font-light text-4xl md:text-6xl text-ink mb-12 md:mb-16"
        >
          {t.countdown.title}
        </motion.h2>

        {parts.ended ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="font-script text-6xl md:text-8xl text-burgundy"
          >
            {t.countdown.ended}
          </motion.div>
        ) : (
          <div
            className="grid grid-cols-4 gap-3 md:gap-8 max-w-3xl mx-auto"
            data-testid="countdown-grid"
          >
            {items.map((it, idx) => (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: 0.15 * idx }}
                className="flex flex-col items-center gap-2 md:gap-3 border-l border-wedding-border/60 first:border-l-0 px-2 md:px-4"
              >
                <span
                  className="font-serif font-light text-4xl sm:text-5xl md:text-7xl text-ink tabular-nums leading-none"
                  data-testid={`countdown-${it.label.toLowerCase()}`}
                >
                  {pad(it.value).padStart(it.digits, "0")}
                </span>
                <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-warm-brown">
                  {it.label}
                </span>
              </motion.div>
            ))}
          </div>
        )}

        <div className="ink-divider mt-16" />
      </div>
    </section>
  );
}
